import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import './LoginPage.css';
import { supabase } from './supabaseClient';
import bcrypt from 'bcryptjs';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loginData, setLoginData] = useState({
    emailOrPhone: '',
    password: ''
  });

  const [loginType, setLoginType] = useState('email');
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    if (!loginData.emailOrPhone || !loginData.password) {
      setLoginError('Please enter both email/phone and password');
      return;
    }

    setIsLoading(true);

    try {
      let user = null;

      // Check if login is by email or phone
      if (loginType === 'email') {
        // Query by email
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('email', loginData.emailOrPhone.toLowerCase())
          .eq('status', 'active')
          .single();

        if (error) throw error;
        user = data;
      } else {
        // Query by phone
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('phone', loginData.emailOrPhone)
          .eq('status', 'active')
          .single();

        if (error) throw error;
        user = data;
      }

      if (!user) {
        setLoginError('Invalid email/phone or password');
        setIsLoading(false);
        return;
      }

      // Verify password
      const isPasswordValid = await bcrypt.compare(loginData.password, user.password_hash);

      if (!isPasswordValid) {
        setLoginError('Invalid email/phone or password');
        setIsLoading(false);
        return;
      }

      // Update last_login timestamp
      await supabase.rpc('update_user_last_login', { user_email: user.email });

      // Store user info in localStorage (without password)
      const userInfo = {
        id: user.id,
        name: user.name,
        email: user.email,
        phone: user.phone
      };
      localStorage.setItem('user', JSON.stringify(userInfo));
      localStorage.setItem('isSignedIn', 'true');
      
      // Dispatch custom event to update header
      window.dispatchEvent(new Event('authStatusChange'));
      
      // If we have a redirect target (e.g. from passenger-info), go there after login
      const redirectTo = location.state?.redirectTo;
      const redirectState = location.state?.redirectState;
      if (redirectTo) {
        navigate(redirectTo, { state: redirectState, replace: true });
      } else {
        // Default: Navigate to dashboard after successful login
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError(error.message || 'Invalid email/phone or password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailOrPhoneChange = (e) => {
    const value = e.target.value;
    setLoginData(prev => ({
      ...prev,
      emailOrPhone: value
    }));
    if (value.includes('@')) {
      setLoginType('email');
    } else if (value.match(/^[0-9+]/)) {
      setLoginType('phone');
    }
  };

  return (
    <div className="login-page">
      <Header />
      
      <section className="login-section">
        <div className="login-container">
          <div className="login-card">
            <div className="login-header">
              <h1 className="login-title">Welcome Back</h1>
              <p className="login-subtitle">
                Login to manage your flights and bookings.
              </p>
            </div>

            <form onSubmit={handleLogin} className="login-form">
              <div className="form-group">
                <label htmlFor="emailOrPhone" className="form-label">
                  {loginType === 'email' ? 'Email' : 'Phone Number'}
                </label>
                <input
                  type={loginType === 'email' ? 'email' : 'tel'}
                  id="emailOrPhone"
                  name="emailOrPhone"
                  value={loginData.emailOrPhone}
                  onChange={handleEmailOrPhoneChange}
                  placeholder={loginType === 'email' ? 'your.email@example.com' : '+234 801 234 5678'}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={loginData.password}
                  onChange={handleInputChange}
                  placeholder="Enter your password"
                  className="form-input"
                  required
                />
              </div>

              <div className="forgot-password-link">
                <Link to="/forgot-password" className="forgot-link">
                  Forgot Password?
                </Link>
              </div>

              {loginError && (
                <div className="error-message" style={{ marginBottom: '1rem', textAlign: 'center' }}>
                  {loginError}
                </div>
              )}

              <button type="submit" className="login-button" disabled={isLoading}>
                {isLoading ? 'Logging in...' : 'Login'}
              </button>
            </form>

            <div className="login-footer">
              <div className="signup-section">
                <p className="signup-text">
                  Don't have an account?{' '}
                  <Link to="/signup" className="signup-link">
                    Sign Up
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LoginPage;

